import {
  RoutePlanFeedbackRatingWeekDTO,
  RoutePlanFeedbackSourceEnum,
} from 'api/model';
import { useRoutePlanFeedbackResults } from 'api/route-plan-feedback/route-plan-feedback';
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import {
  buildFeedbackPageUrl,
  changeTagLabel,
  formatPlanningDate,
} from './feedback-round-helpers';
import { MAX_RATING, ratingLabel, ratingStars } from '../rating';

const Wrap = styled.div`
  display: grid;
  gap: 16px;
`;

const Controls = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`;

const Stats = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;

  @media (max-width: 760px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const Stat = styled.div`
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.primary};
  border-radius: 10px;
  padding: 12px 14px;
  display: grid;
  gap: 2px;
`;

const StatNumber = styled.span`
  font-size: 1.6rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
`;

const StatLabel = styled.span`
  font-size: 0.82rem;
  color: ${({ theme }) => theme.colors.foreground.tertiary};
`;

const TableWrap = styled.div`
  overflow-x: auto;
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.primary};
  border-radius: 10px;
`;

const Table = styled.table`
  border-collapse: collapse;
  width: 100%;
  min-width: 760px;
  font-size: 0.88rem;

  th,
  td {
    text-align: left;
    vertical-align: top;
    padding: 8px 12px;
    border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
  }
  th {
    font-weight: 600;
    color: ${({ theme }) => theme.colors.foreground.secondary};
  }
  tr:last-child td {
    border-bottom: 0;
  }
`;

const Muted = styled.span`
  color: ${({ theme }) => theme.colors.foreground.tertiary};
`;

const PeriodButton = styled.button<{ selected: boolean }>`
  border: 1px solid ${({ theme }) => theme.colors.border.primary};
  background: ${({ selected, theme }) =>
    selected
      ? theme.colors.background.highlight
      : theme.colors.background.primary};
  color: ${({ theme }) => theme.colors.foreground.primary};
  border-radius: 999px;
  padding: 4px 12px;
  font: inherit;
  cursor: pointer;
`;

const PERIODS = [7, 14, 30, 90];

const Section = styled.div`
  display: grid;
  gap: 8px;
`;

const SectionTitle = styled.h3`
  margin: 0;
  font-size: 1rem;
`;

const Stars = styled.span`
  color: rgb(230, 160, 20);
  letter-spacing: 1px;
  white-space: nowrap;
`;

const Num = styled.td`
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;

/** "3,7" — svensk decimal, en decimal. */
const formatAverage = (value: number | null | undefined): string =>
  value === null || value === undefined
    ? '–'
    : value.toLocaleString('sv-SE', {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      });

const averageCell = (average: number | null | undefined, count: number) =>
  count > 0 ? `${formatAverage(average)} (${count})` : '–';

/** Pil mot förra veckan med betyg, när båda veckorna har ett snitt. */
const change = (
  weeks: RoutePlanFeedbackRatingWeekDTO[],
  index: number
): string => {
  const current = weeks[index].allAverage;
  if (current === null || current === undefined) return '';
  const previous = weeks
    .slice(0, index)
    .reverse()
    .find(
      (w) => w.allAverage !== null && w.allAverage !== undefined
    )?.allAverage;
  if (previous === null || previous === undefined) return '';
  const diff = current - previous;
  if (Math.abs(diff) < 0.05) return '→';
  return diff > 0 ? `↑ ${formatAverage(diff)}` : `↓ ${formatAverage(-diff)}`;
};

const isoDate = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate()
  ).padStart(2, '0')}`;

interface Props {
  namesVisible: boolean;
}

/** Svaren över tid. Namnen syns bara för namnläsarna — servern skickar dem inte till andra. */
const ResultsView: React.FC<Props> = ({ namesVisible }) => {
  const [days, setDays] = useState(14);
  const params = useMemo(() => {
    const to = new Date();
    to.setDate(to.getDate() + 1); // morgondagens prov räknas med
    const from = new Date();
    from.setDate(from.getDate() - days);
    return { from: isoDate(from), to: isoDate(to) };
  }, [days]);

  const { data, isLoading, isError } = useRoutePlanFeedbackResults(params);

  // Listan har även feedbackknappens betyg; talen överst gäller ruttprovet.
  const total =
    data?.answers.filter((a) => a.source !== RoutePlanFeedbackSourceEnum.Button)
      .length ?? 0;
  // Svar från före betyget (de tre gamla svaren) finns bland svaren men inte i fördelningen.
  const ratedCount = data?.ratings.reduce((sum, r) => sum + r.count, 0) ?? 0;
  // Bara veckor från och med den första med något betyg — trenden börjar där mätningen gör.
  const trend = useMemo(() => {
    const weeks = data?.trend ?? [];
    const first = weeks.findIndex(
      (w) => w.sampleCount > 0 || w.buttonCount > 0
    );
    return first < 0 ? [] : weeks.slice(first);
  }, [data]);

  return (
    <Wrap>
      <Controls>
        <span>Period:</span>
        {PERIODS.map((p) => (
          <PeriodButton
            key={p}
            selected={p === days}
            onClick={() => setDays(p)}
          >
            {p} dagar
          </PeriodButton>
        ))}
      </Controls>

      {isLoading && <Muted>Hämtar svar …</Muted>}
      {isError && <Muted>Svaren kunde inte hämtas.</Muted>}

      {data && (
        <>
          <Stats>
            <Stat>
              <StatNumber>{total}</StatNumber>
              <StatLabel>svar på ruttproven</StatLabel>
            </Stat>
            <Stat>
              <StatNumber>{data.distinctResponders}</StatNumber>
              <StatLabel>personer har svarat</StatLabel>
            </Stat>
            <Stat>
              <StatNumber>
                {data.promptsShown > 0
                  ? `${Math.round((100 * data.promptsAnswered) / data.promptsShown)} %`
                  : '–'}
              </StatNumber>
              <StatLabel>av {data.promptsShown} pingar fick svar</StatLabel>
            </Stat>
            <Stat>
              <StatNumber>
                {formatAverage(data.averageRating)}
                {data.averageRating !== null &&
                data.averageRating !== undefined ? (
                  <Muted> / {MAX_RATING}</Muted>
                ) : null}
              </StatNumber>
              <StatLabel>snittbetyg på ruttproven</StatLabel>
            </Stat>
          </Stats>

          {ratedCount > 0 && (
            <div>
              <b>Fördelning: </b>
              {[...data.ratings]
                .sort((a, b) => b.rating - a.rating)
                .map((r) => (
                  <span key={r.rating}>
                    <Stars title={ratingLabel(r.rating)}>
                      {ratingStars(r.rating)}
                    </Stars>{' '}
                    {r.count}
                    {r.rating > 1 ? ' · ' : ''}
                  </span>
                ))}
            </div>
          )}

          <Section>
            <SectionTitle>Blir förslagen bättre?</SectionTitle>
            <Muted>
              Snittbetyg per vecka (planeringsdagens vecka) från ruttprovet och
              från feedbackknappen i ruttplaneringen – samma skala 1–5. Antal
              betyg inom parentes.
            </Muted>
            {trend.length === 0 ? (
              <Muted>Inga betyg än.</Muted>
            ) : (
              <TableWrap>
                <Table>
                  <thead>
                    <tr>
                      <th>Vecka</th>
                      <th>Ruttprovet</th>
                      <th>Feedbackknappen</th>
                      <th>Totalt</th>
                      <th>Mot förra veckan</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trend.map((w, i) => (
                      <tr key={w.weekStartString}>
                        <td>
                          v. {w.weekNumber} <Muted>({w.weekStartString})</Muted>
                        </td>
                        <Num>{averageCell(w.sampleAverage, w.sampleCount)}</Num>
                        <Num>{averageCell(w.buttonAverage, w.buttonCount)}</Num>
                        <Num>
                          <b>
                            {averageCell(
                              w.allAverage,
                              w.sampleCount + w.buttonCount
                            )}
                          </b>
                        </Num>
                        <Num>{change(trend, i)}</Num>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </TableWrap>
            )}
          </Section>

          {data.tags.length > 0 && (
            <div>
              <b>Vanligaste ändringarna: </b>
              {data.tags
                .map((t) => `${changeTagLabel(t.tag)} (${t.count})`)
                .join(' · ')}
            </div>
          )}

          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <th>Dag</th>
                  <th>Paket</th>
                  <th>Betyg</th>
                  <th>Ändringar</th>
                  <th>Kommentar</th>
                  {namesVisible && <th>Svarat av</th>}
                </tr>
              </thead>
              <tbody>
                {data.answers.length === 0 && (
                  <tr>
                    <td colSpan={namesVisible ? 6 : 5}>
                      <Muted>Inga svar under perioden ännu.</Muted>
                    </td>
                  </tr>
                )}
                {data.answers.map((a) => {
                  // Knappens betyg gäller förslaget TL hade framför sig, inte ett sparat prov,
                  // så det finns ingen provsida att länka till.
                  const fromButton =
                    a.source === RoutePlanFeedbackSourceEnum.Button;
                  return (
                    <tr key={`${a.source}-${a.answerId}`}>
                      <td>
                        {formatPlanningDate(a.planningDateString)}
                        <br />
                        <Muted>
                          {fromButton ? 'feedbackknappen' : 'prov'}{' '}
                          {a.sampleTimeString}
                        </Muted>
                      </td>
                      <td>
                        {fromButton ? (
                          `#${a.packageIndex}`
                        ) : (
                          <Link
                            to={buildFeedbackPageUrl(
                              a.planningDateString,
                              a.packageIndex,
                              undefined,
                              a.routePlanRunId
                            )}
                          >
                            #{a.packageIndex}
                          </Link>
                        )}
                        {a.fieldTesterName ? ` · ${a.fieldTesterName}` : ''}
                        {a.route && (
                          <>
                            <br />
                            <Muted>{a.route}</Muted>
                          </>
                        )}
                      </td>
                      <td>
                        {a.rating ? (
                          <>
                            <Stars>{ratingStars(a.rating)}</Stars>{' '}
                            {ratingLabel(a.rating)}
                          </>
                        ) : (
                          <Muted>{a.legacyAnswer ?? '–'}</Muted>
                        )}
                        {a.source === RoutePlanFeedbackSourceEnum.Toast && (
                          <>
                            <br />
                            <Muted>i toasten</Muted>
                          </>
                        )}
                      </td>
                      <td>{a.changeTags.map(changeTagLabel).join(', ')}</td>
                      <td>{a.comment}</td>
                      {namesVisible && <td>{a.answeredByName}</td>}
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </TableWrap>
        </>
      )}
    </Wrap>
  );
};

export default ResultsView;
